import React from 'react';
import {
  GestureResponderEvent,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import type { IconProps } from '../../icons/types';
import { borderRadius, colors, spacing, typography } from '../../tokens';

// Alert icon with solid orange circle and white exclamation (from Figma)
const AlertIcon: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <Svg width={size} height={size} viewBox="0 0 28 28" fill="none">
    {/* Solid orange circle background */}
    <Path
      d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28Z"
      fill="#E24D2A"
    />
    {/* White exclamation mark */}
    <Path
      d="M14 8V15"
      stroke="#FCFCFC"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <Path
      d="M14 19V19.5"
      stroke="#FCFCFC"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </Svg>
);

// X close icon (from Figma)
const CloseIcon: React.FC<{ color?: string; size?: number }> = ({
  color = '#FCFCFC',
  size = 16,
}) => (
  <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <Path
      d="M12 4L4 12M4 4L12 12"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export type PillType = 'default' | 'hover' | 'selected' | 'selectedLabel';

export interface PillProps {
  /**
   * The text label displayed in the pill
   */
  label: string;

  /**
   * Optional custom icon component to display before the label
   * If not provided, uses the default alert icon
   */
  icon?: React.ComponentType<IconProps>;

  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether the pill is in a selected state
   */
  selected?: boolean;

  /**
   * Whether to show the close (X) button instead of icon when selected
   * This creates the "Selected Label" variant from Figma
   */
  showClose?: boolean;

  /**
   * Callback when the pill is pressed
   */
  onPress?: (e: GestureResponderEvent) => void;

  /**
   * Callback when the close button is pressed (only for selectedLabel state)
   */
  onClose?: (e: GestureResponderEvent) => void;

  /**
   * Whether the pill is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const Pill: React.FC<PillProps> = ({
  label,
  icon: CustomIcon,
  showIcon = true,
  selected = false,
  showClose = false,
  onPress,
  onClose,
  disabled = false,
  testID,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  // Determine the pill type/state
  const isSelectedLabel = selected && showClose;
  const shouldShowIcon = showIcon && !isSelectedLabel;

  // Determine background color based on state
  const getBackgroundColor = () => {
    if (selected) {
      return colors.neutral[800]; // #020202
    }
    if (isHovered && !disabled) {
      return colors.slate[100]; // #ECEEF1
    }
    return colors.neutral[100]; // #F3F3F3
  };

  // Determine text color based on state
  const getTextColor = () => {
    if (selected) {
      return colors.brand.white; // #FCFCFC
    }
    return colors.neutral[800]; // #020202
  };

  const handlePress = (e: GestureResponderEvent) => {
    if (isSelectedLabel && onClose) {
      onClose(e);
    } else if (onPress) {
      onPress(e);
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.container,
        { backgroundColor: getBackgroundColor() },
        disabled && styles.disabled,
      ]}
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
    >
      {shouldShowIcon &&
        (CustomIcon ? (
          <View style={styles.iconContainer}>
            <CustomIcon
              size={16}
              color={selected ? colors.brand.white : colors.neutral[800]}
            />
          </View>
        ) : (
          <AlertIcon size={24} />
        ))}
      <Text
        style={[styles.label, { color: getTextColor() }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {label}
      </Text>
      {isSelectedLabel && (
        <View style={styles.closeContainer}>
          <CloseIcon color={colors.brand.white} size={16} />
        </View>
      )}
    </Pressable>
  );
};

Pill.displayName = 'Pill';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: borderRadius.full,
    gap: spacing.sm,
    alignSelf: 'flex-start',
    maxWidth: '100%', // Respect parent container width
  },
  iconContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    ...typography.body.small,
    fontWeight: '500',
    ...Platform.select({
      web: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    }),
  },
  disabled: {
    opacity: 0.5,
  },
});
